import React from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../shared/components/forms/inputs/InputField';
import {
  ProcessingMode,
  ValidationErrors
} from '../types/firstProcessing.types';

interface ProcessingMethodSectionProps {
  mode: ProcessingMode;
  intrplMax: string;
  onModeChange: (value: ProcessingMode) => void;
  onIntrplMaxChange: (value: string) => void;
  validationErrors: ValidationErrors;
}

// Processing mode options matching ProcessingMode type
const PROCESSING_MODES: ProcessingMode[] = ['mean', 'nearest', 'nearest (mean)', 'intrpl'];

const ProcessingMethodSection: React.FC<ProcessingMethodSectionProps> = ({
  mode,
  intrplMax,
  onModeChange,
  onIntrplMaxChange,
  validationErrors
}) => {
  const { t } = useTranslation('firstProcessing');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Section Header */}
      <div className="border-b pb-2">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-foreground">
          {t('methodSelection.title')}
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          {t('methodSelection.description')}
        </p>
      </div>

      {/* Method Selection Cards */}
      <div className="space-y-3 sm:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
          {PROCESSING_MODES.map((modeValue) => (
            <div
              key={modeValue}
              onClick={() => onModeChange(modeValue)}
              className={`
                relative border rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-200
                ${mode === modeValue
                  ? 'border-primary bg-accent shadow-md'
                  : 'border-border hover:border-border/60 hover:shadow-sm'
                }
              `}
            >
              {/* Tooltip info icon — top-right of card */}
              <div
                className="absolute top-2 right-2 group"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground p-1 rounded-full"
                  aria-label={t(`methodSelection.modes.${modeValue}.label`)}
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 sm:w-72 p-2 sm:p-3 bg-gray-900 text-white text-[10px] sm:text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 pointer-events-none">
                  <p>{t(`methodSelection.modes.${modeValue}.tooltip`)}</p>
                  <div className="absolute bottom-full right-3 w-2 h-2 bg-gray-900 transform rotate-45 translate-y-1"></div>
                </div>
              </div>

              <div className="flex items-start justify-between pr-6 sm:pr-7">
                <div className="flex-1">
                  <h4 className={`text-xs sm:text-sm md:text-base font-medium ${mode === modeValue ? 'text-accent-foreground' : 'text-foreground'}`}>
                    {t(`methodSelection.modes.${modeValue}.label`)}
                  </h4>
                  <p className={`text-[10px] sm:text-xs md:text-sm mt-0.5 sm:mt-1 ${mode === modeValue ? 'text-primary' : 'text-muted-foreground'}`}>
                    {t(`methodSelection.modes.${modeValue}.description`)}
                  </p>
                </div>
                <div className="ml-2 sm:ml-3">
                  {mode === modeValue ? (
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-input"></div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Validation Error */}
        {validationErrors.mode && (
          <p className="mt-2 text-xs sm:text-sm text-red-600 flex items-center">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {validationErrors.mode || t('methodSelection.validationError')}
          </p>
        )}

        {/* Interpolation Max Input (conditional) */}
        {mode === 'intrpl' && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="ml-2 sm:ml-3 flex-1">
                <h4 className="text-xs sm:text-sm font-medium text-yellow-900 mb-1 sm:mb-2">
                  {t('methodSelection.interpolationSettings')}
                </h4>
                <InputField
                  label={t('methodSelection.intrplMax')}
                  value={intrplMax}
                  onChange={onIntrplMaxChange}
                />
                {validationErrors.intrplMax && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">
                    {validationErrors.intrplMax}
                  </p>
                )}
                <p className="mt-1 sm:mt-2 text-[10px] sm:text-xs text-yellow-700">
                  {t('methodSelection.interpolationHelp')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessingMethodSection;
